import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import {
  materialSettingsAtom,
  roomModeSettingsAtom,
  roomTypeSettingsAtom,
  settingsChangedAtom,
} from '../../atoms/settings';
import type { TYPE_MATERIAL_OPTIONS } from '../../constants/colors-and-materials';
import { ROOM_MODE_OPTIONS } from '../../constants/room-mode';
import { getAvailableMaterials } from '../../utils/room-specific-options';
import { SettingsSectionHeader } from './settings-section-header';

const lightMaterials = [
  'marble',
  'glass',
  'porcelain',
  'porcelain stoneware',
  'quartz',
  'laminate',
  'white ash',
  'decorative melamine',
  'bwp plywood',
  'mdf',
];

type MaterialChipProps = {
  material: TYPE_MATERIAL_OPTIONS;
  isSelected: boolean;
  onSelect: (material: TYPE_MATERIAL_OPTIONS) => void;
};

const MaterialChip = ({ material, isSelected, onSelect }: MaterialChipProps) => {
  const { t } = useTranslation();
  const isLightMaterial = lightMaterials.includes(material.value);

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => onSelect(material)}
      className={cn(
        'h-6 justify-start gap-1 rounded-full border border-slate-200 bg-white px-1.5 text-[10px] font-medium text-slate-600 hover:border-primary-light hover:bg-white',
        isSelected &&
          'border-primary-light bg-primary-light/10 text-slate-700 ring-1 ring-primary-light'
      )}
    >
      <span
        className="flex size-3.5 items-center justify-center rounded-full border border-slate-200"
        style={{ backgroundColor: material.hex }}
      >
        {isSelected ? (
          <Check
            aria-hidden
            className={cn('!size-2.5', isLightMaterial ? 'text-slate-500' : 'text-white')}
          />
        ) : null}
      </span>
      <span>{t(`materialsObject.${material.translation}`)}</span>
    </Button>
  );
};

export const MaterialSettings = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [materials, setMaterials] = useAtom(materialSettingsAtom);
  const mode = useAtomValue(roomModeSettingsAtom);
  const roomType = useAtomValue(roomTypeSettingsAtom);
  const setSettingsChanged = useSetAtom(settingsChangedAtom);

  const supportsMaterials =
    mode === ROOM_MODE_OPTIONS.CHANGE_STYLE.value ||
    mode === ROOM_MODE_OPTIONS.FURNISH_EMPTY_ROOM.value;
  const isDisabled = !supportsMaterials || !roomType;
  const selectedMaterials = Array.isArray(materials) ? materials : [];
  const materialOptions = getAvailableMaterials(roomType);

  useEffect(() => {
    if (isDisabled) {
      setIsOpen(false);
      setMaterials([]);
    }
  }, [isDisabled, setMaterials]);

  if (!supportsMaterials) {
    return null;
  }

  const handleSelectMaterial = (material: TYPE_MATERIAL_OPTIONS) => {
    const nextMaterials = selectedMaterials.includes(material)
      ? selectedMaterials.filter((selectedMaterial) => selectedMaterial !== material)
      : [...selectedMaterials, material];

    setMaterials(nextMaterials);
    setSettingsChanged(true);
  };

  return (
    <div
      className={cn(
        'w-full rounded-sm border border-slate-200 bg-white sm:w-[287px]',
        isDisabled && 'bg-slate-50 text-slate-400'
      )}
    >
      <SettingsSectionHeader
        label={t('materials')}
        count={!isDisabled ? selectedMaterials.length : undefined}
        isOpen={isOpen}
        disabled={isDisabled}
        onClick={() => setIsOpen((current) => !current)}
      />

      {isOpen && !isDisabled && (
        <div className="flex flex-wrap gap-1 px-3 pb-3">
          {materialOptions.map((material) => (
            <MaterialChip
              key={material.value}
              material={material}
              isSelected={selectedMaterials.includes(material)}
              onSelect={handleSelectMaterial}
            />
          ))}
        </div>
      )}
    </div>
  );
};
